/**
 * Status / tag pill. Audit: status pills were rounded-full, rounded-lg and
 * rounded-md on sibling pages. One shape: rounded-full.
 *
 * Tone meaning (keep it — colour is how staff scan a list):
 *   slate   neutral / closed / not started
 *   brand   in progress / informational
 *   emerald done / approved / paid
 *   amber   waiting on someone / needs attention soon
 *   rose    problem / rejected / overdue / cancelled
 *   violet  special category (promotion, VIP) — sparingly
 */
const TONE = {
  slate: 'bg-slate-100 text-slate-600 ring-slate-200',
  brand: 'bg-brand-50 text-brand-700 ring-brand-200',
  emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  amber: 'bg-amber-50 text-amber-800 ring-amber-200',
  rose: 'bg-rose-50 text-rose-700 ring-rose-200',
  violet: 'bg-violet-50 text-violet-700 ring-violet-200',
}
const DOT = {
  slate: 'bg-slate-400', brand: 'bg-brand-500', emerald: 'bg-emerald-500',
  amber: 'bg-amber-500', rose: 'bg-rose-500', violet: 'bg-violet-500',
}

/**
 * `color` (any CSS colour, e.g. a payment method's or appointment type's own
 * colour from the data) overrides `tone` — tinted background, darkened text.
 */
export function Badge({ tone = 'slate', color, dot = false, className = '', children, ...rest }) {
  const style = color ? {
    backgroundColor: `color-mix(in srgb, ${color} 12%, white)`,
    color: `color-mix(in srgb, ${color} 75%, black)`,
    boxShadow: `inset 0 0 0 1px color-mix(in srgb, ${color} 35%, white)`,
  } : undefined
  return (
    <span style={style} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[12px] font-medium whitespace-nowrap ${color ? '' : `ring-1 ring-inset ${TONE[tone] || TONE.slate}`} ${className}`} {...rest}>
      {dot && <span className={`w-1.5 h-1.5 rounded-full ${color ? '' : DOT[tone] || DOT.slate}`} style={color ? { backgroundColor: color } : undefined} />}
      {children}
    </span>
  )
}

/**
 * Count bubble for tabs and menus.
 *   tone 'alert' — something waits on THIS user (solid rose)
 *   tone 'count' — just a number (grey)
 * Renders nothing for 0 / null — never a "0" bubble. `unknown` (a count that failed to
 * load) shows a grey '—', so a failure never looks the same as zero.
 */
export function CountBadge({ n, tone = 'count', unknown = false }) {
  if (unknown) return <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-400" title="โหลดจำนวนไม่สำเร็จ">—</span>
  if (!n) return null
  const cls = tone === 'alert' ? 'bg-rose-500 text-white' : 'bg-slate-100 text-slate-500'
  return (
    <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[11px] font-semibold tabular-nums ${cls}`}>
      {n > 99 ? '99+' : n}
    </span>
  )
}
